import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, CheckCircle2, FileText, CreditCard, Search, Mail, X, Trash2, Plus, Copy, Send } from 'lucide-react';
import { toast as sonnerToast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_TYPE_OPTIONS = [
  { value: '', label: 'Autre / non spécifié' },
  { value: 'coaching-online', label: 'Coaching 100% en ligne (app)' },
  { value: 'coaching-hybride', label: 'Coaching Hybride (app)' },
  { value: 'communaute', label: 'Communauté Body-Up (app)' },
  { value: 'suivi-pro-plus', label: 'Suivi Pro+ (app)' },
  { value: 'personal-training', label: 'Personal Training (présentiel)' },
  { value: 'duo-training', label: 'Duo Training (présentiel)' },
  { value: 'ems-training', label: 'EMS Training (présentiel)' },
  { value: 'ems-duo-training', label: 'EMS Duo (présentiel)' },
  { value: 'scan-3d', label: 'Scan 3D (présentiel)' },
  { value: 'plan-alimentaire', label: 'Plan alimentaire (présentiel)' },
  { value: 'hypoxie-fitsonic', label: 'Hypoxie / Fitsonic (présentiel)' },
];
const PRODUCT_TYPE_LABEL = Object.fromEntries(PRODUCT_TYPE_OPTIONS.map((o) => [o.value, o.label]));
const PLAN_LABEL = { transformation: 'Coaching 100% en ligne', vip: 'Coaching Hybride', communaute: 'Communauté Body-Up', solo: 'Suivi Pro+', subscription: 'Suivi Pro', monthly: 'Suivi Pro mensuel', yearly: 'Suivi Pro annuel' };

/**
 * Onglet Paiements du dashboard admin Body-Up.
 * Affiche factures (virement) + sessions Stripe, avec bouton "Marquer comme payé"
 * qui déclenche l'email "forfait activé" au client. Permet aussi de créer une facture
 * QR-bill manuelle (traçabilité des arrangements hors self-checkout, ex: mensualités).
 */
export const AdminPaymentsPanel = ({ csrfToken, onLogout, fmtDate }) => {
  const [data, setData] = useState({ invoices: [], sessions: [], subscriptions: [], stats: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | pending | paid
  const [confirmModal, setConfirmModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [manualInvoiceModal, setManualInvoiceModal] = useState(null); // null | {} | {prefill}
  const [upsellLoading, setUpsellLoading] = useState(false);
  const [vipUpsellLoading, setVipUpsellLoading] = useState(false);

  const fetchWithCredentials = (url) => fetch(url, { credentials: 'include' });
  const fetchWithCsrf = (url, options = {}) => fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...options.headers,
      'X-CSRF-Token': csrfToken,
      'Content-Type': 'application/json',
    },
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithCredentials(`${BACKEND_URL}/api/admin/payments`);
      if (res.status === 401) { onLogout(); return; }
      const json = await res.json();
      setData(json);
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { loadData(); }, [loadData]);

  const markPaid = async (kind, id, note = '') => {
    setActionLoading(true);
    try {
      const endpoint = kind === 'invoice'
        ? `${BACKEND_URL}/api/admin/payments/mark-invoice-paid/${id}`
        : `${BACKEND_URL}/api/admin/payments/mark-session-paid/${id}`;
      const res = await fetchWithCsrf(endpoint, {
        method: 'POST',
        body: JSON.stringify({ note }),
      });
      if (res.status === 401) { onLogout(); return; }
      const json = await res.json();
      if (json.success) {
        const planNote = json.granted_plan ? ` Accès "${PLAN_LABEL[json.granted_plan] || json.granted_plan}" prolongé de 30 jours.` : '';
        setToast({
          type: 'success',
          msg: json.already_paid
            ? 'Déjà marqué comme payé, aucun email renvoyé.'
            : `Marqué comme payé. Email "forfait activé" envoyé à ${json.email_sent_to}.${planNote}`,
        });
        await loadData();
      } else {
        setToast({ type: 'error', msg: 'Erreur lors du marquage.' });
      }
    } catch {
      setToast({ type: 'error', msg: 'Erreur réseau.' });
    } finally {
      setActionLoading(false);
      setConfirmModal(null);
      setTimeout(() => setToast(null), 6000);
    }
  };

  const matchSearch = (text) => !search || (text || '').toLowerCase().includes(search.toLowerCase());

  const deletePayment = async (kind, id) => {
    if (!window.confirm('Supprimer définitivement ce paiement ? Cette action est irréversible.')) return;
    const endpoint = kind === 'invoice'
      ? `${BACKEND_URL}/api/admin/payments/invoice/${encodeURIComponent(id)}`
      : `${BACKEND_URL}/api/admin/payments/session/${encodeURIComponent(id)}`;
    try {
      const res = await fetchWithCsrf(endpoint, { method: 'DELETE' });
      if (res.status === 401) { onLogout(); return; }
      if (!res.ok) { setToast({ type: 'error', msg: 'Erreur lors de la suppression.' }); setTimeout(() => setToast(null), 6000); return; }
      setData((d) => ({
        ...d,
        invoices: kind === 'invoice' ? d.invoices.filter((i) => i.invoice_number !== id) : d.invoices,
        sessions: kind === 'session' ? d.sessions.filter((s) => s.session_id !== id) : d.sessions,
      }));
      setToast({ type: 'success', msg: 'Paiement supprimé.' });
      setTimeout(() => setToast(null), 4000);
    } catch {
      setToast({ type: 'error', msg: 'Erreur réseau.' });
      setTimeout(() => setToast(null), 6000);
    }
  };

  const sendScheduledNow = async (invoiceNumber) => {
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/payments/send-scheduled-now/${encodeURIComponent(invoiceNumber)}`, { method: 'POST' });
      if (res.status === 401) { onLogout(); return; }
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Erreur');
      sonnerToast.success('Facture envoyée immédiatement.');
      await loadData();
    } catch (e) {
      sonnerToast.error(e.message || 'Erreur réseau.');
    }
  };

  const notifySoloUpsell = async () => {
    if (!window.confirm('Envoyer l\'email "Passez à Suivi Pro+" à tous les clients Suivi Pro actifs qui ne l\'ont pas encore reçu ?')) return;
    setUpsellLoading(true);
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/payments/notify-solo-upsell`, { method: 'POST' });
      if (res.status === 401) { onLogout(); return; }
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Erreur');
      sonnerToast.success(
        json.sent > 0
          ? `Email envoyé à ${json.sent} client(s) Suivi Pro.`
          : 'Aucun nouveau client à notifier (déjà envoyé à tous).'
      );
    } catch (e) {
      sonnerToast.error(e.message || 'Erreur réseau.');
    } finally {
      setUpsellLoading(false);
    }
  };

  const notifyVipUpsell = async () => {
    if (!window.confirm('Envoyer l\'email "Passez au pack VIP" à tous les clients Communauté actifs qui ne l\'ont pas encore reçu ?')) return;
    setVipUpsellLoading(true);
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/payments/notify-vip-upsell`, { method: 'POST' });
      if (res.status === 401) { onLogout(); return; }
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Erreur');
      sonnerToast.success(
        json.sent > 0
          ? `Email envoyé à ${json.sent} client(s) Communauté.`
          : 'Aucun nouveau client à notifier (déjà envoyé à tous).'
      );
    } catch (e) {
      sonnerToast.error(e.message || 'Erreur réseau.');
    } finally {
      setVipUpsellLoading(false);
    }
  };


  const filteredInvoices = data.invoices.filter((i) => {
    if (filter === 'pending' && i.status !== 'pending') return false;
    if (filter === 'paid' && i.status !== 'paid') return false;
    return matchSearch(i.customer_name) || matchSearch(i.customer_email) || matchSearch(i.invoice_number);
  });
  const filteredSessions = data.sessions.filter((s) => {
    if (filter === 'pending' && s.payment_status === 'paid') return false;
    if (filter === 'paid' && s.payment_status !== 'paid') return false;
    return matchSearch(s.customer_name) || matchSearch(s.customer_email) || matchSearch(s.description);
  });
  const filteredSubscriptions = (data.subscriptions || []).filter((t) => {
    if (filter === 'pending' && t.payment_status === 'paid') return false;
    if (filter === 'paid' && t.payment_status !== 'paid') return false;
    return matchSearch(t.email) || matchSearch(t.plan);
  });

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;
  }

  const s = data.stats || {};

  return (
    <div data-testid="admin-payments-panel" className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <StatCard label="Factures en attente" value={s.pending_invoices || 0} color="#FBBF24" />
        <StatCard label="Factures payées" value={s.paid_invoices || 0} color="#10B981" />
        <StatCard label="Stripe payés" value={s.paid_stripe || 0} color="#8B5CF6" />
        <StatCard label="Stripe en attente" value={s.pending_stripe || 0} color="#71717a" />
        <StatCard label="Abonnements payés" value={s.paid_subscriptions || 0} color="#22D3EE" />
        <StatCard label="CA encaissé" value={`${(s.revenue_paid_chf || 0).toFixed(0)} CHF`} color="#E91E63" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (nom, email, n° facture)…"
            className="w-full pl-10 pr-3 py-2 bg-zinc-900 border border-white/10 rounded-xl text-white placeholder-zinc-500 focus:border-primary focus:outline-none text-sm"
            data-testid="admin-payments-search"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'paid'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${
                filter === f ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
              data-testid={`admin-payments-filter-${f}`}
            >
              {f === 'all' ? 'Tous' : f === 'pending' ? 'En attente' : 'Payés'}
            </button>
          ))}
        </div>
        <button
          onClick={() => setManualInvoiceModal({})}
          className="px-3 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-primary to-pink-500 inline-flex items-center gap-1.5 hover:scale-105 transition-transform"
          data-testid="new-manual-invoice-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Nouvelle facture manuelle
        </button>
        <button
          onClick={notifySoloUpsell}
          disabled={upsellLoading}
          className="px-3 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-sky-500 to-primary inline-flex items-center gap-1.5 hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100"
          data-testid="notify-solo-upsell-btn"
          title="Envoie l'email « Passez à Suivi Pro+ » aux clients Suivi Pro actifs pas encore notifiés"
        >
          {upsellLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Notifier upsell Suivi Pro+
        </button>
        <button
          onClick={notifyVipUpsell}
          disabled={vipUpsellLoading}
          className="px-3 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-fuchsia-600 to-primary inline-flex items-center gap-1.5 hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100"
          data-testid="notify-vip-upsell-btn"
          title="Envoie l'email « Passez au pack VIP » aux clients Communauté actifs pas encore notifiés"
        >
          {vipUpsellLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Notifier upsell VIP
        </button>
      </div>

      {toast && (
        <div className={`p-4 rounded-xl text-sm flex items-start gap-3 ${
          toast.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-300' : 'bg-red-500/10 border border-red-500/30 text-red-300'
        }`} data-testid="admin-payments-toast">
          {toast.type === 'success' ? <CheckCircle2 className="w-5 h-5 flex-shrink-0" /> : <X className="w-5 h-5 flex-shrink-0" />}
          <span>{toast.msg}</span>
        </div>
      )}

      {/* Factures (virement) */}
      <Section title={<><FileText className="w-5 h-5 inline mr-2 text-blue-400" />Factures virement bancaire ({filteredInvoices.length})</>}>
        <PaymentsTable
          rows={filteredInvoices}
          columns={['N° facture', 'Date', 'Client', 'Email', 'Forfait', 'Montant', 'Statut', 'Action']}
          renderRow={(i) => (
            <tr key={i.invoice_number} className="border-t border-white/5 text-zinc-300" data-testid={`invoice-row-${i.invoice_number}`}>
              <td className="px-4 py-3 font-mono text-xs">{i.invoice_number}</td>
              <td className="px-4 py-3 whitespace-nowrap">{fmtDate(i.created_at)}</td>
              <td className="px-4 py-3">{i.customer_name}</td>
              <td className="px-4 py-3 text-xs">{i.customer_email}</td>
              <td className="px-4 py-3 text-xs text-zinc-400">
                {PRODUCT_TYPE_LABEL[i.product_type] || i.product_type || '—'}
                {i.installment_total > 1 && <div className="text-zinc-500">Mensualité {i.installment_number}/{i.installment_total}</div>}
              </td>
              <td className="px-4 py-3 font-semibold text-white">{(i.total || 0).toFixed(2)} CHF</td>
              <td className="px-4 py-3">
                <StatusBadge status={i.status} />
                {i.status === 'scheduled' && i.scheduled_date && (
                  <div className="text-zinc-500 text-[11px] mt-1">Prévue le {fmtDate(i.scheduled_date)}</div>
                )}
                {i.status === 'scheduled' && i.reminder_sent && (
                  <div className="text-amber-400 text-[11px] mt-0.5 inline-flex items-center gap-1" data-testid={`reminder-sent-${i.invoice_number}`}>
                    <CheckCircle2 className="w-3 h-3" /> Rappel J-3 envoyé
                  </div>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  {i.status === 'scheduled' ? (
                    <button
                      onClick={() => sendScheduledNow(i.invoice_number)}
                      className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white text-xs rounded-lg font-semibold transition-colors inline-flex items-center gap-1"
                      data-testid={`send-scheduled-now-${i.invoice_number}`}
                    >
                      <Mail className="w-3.5 h-3.5" /> Envoyer maintenant
                    </button>
                  ) : i.status === 'pending' ? (
                    <button
                      onClick={() => setConfirmModal({ kind: 'invoice', id: i.invoice_number, label: i.customer_name, amount: i.total })}
                      className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs rounded-lg font-semibold hover:scale-105 transition-transform inline-flex items-center gap-1"
                      data-testid={`mark-paid-${i.invoice_number}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Marquer payée
                    </button>
                  ) : (
                    <span className="text-green-400 text-xs inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> {fmtDate(i.paid_at)}
                    </span>
                  )}
                  <button
                    onClick={() => setManualInvoiceModal({
                      customer_name: i.customer_name, customer_email: i.customer_email,
                      description: i.items?.[0]?.description || '', amount: i.total,
                      product_type: i.product_type || '',
                    })}
                    className="p-1.5 text-zinc-500 hover:text-primary transition-colors"
                    data-testid={`duplicate-invoice-${i.invoice_number}`}
                    title="Dupliquer (ex: mensualité suivante)"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => deletePayment('invoice', i.invoice_number)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`delete-invoice-${i.invoice_number}`} title="Supprimer">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          )}
        />
      </Section>

      {/* Sessions Stripe */}
      <Section title={<><CreditCard className="w-5 h-5 inline mr-2 text-violet-400" />Sessions Stripe ({filteredSessions.length})</>}>
        <PaymentsTable
          rows={filteredSessions}
          columns={['Date', 'Client', 'Forfait', 'Montant', 'Mensualités', 'Statut', 'Action']}
          renderRow={(s) => (
            <tr key={s.session_id} className="border-t border-white/5 text-zinc-300" data-testid={`session-row-${s.session_id}`}>
              <td className="px-4 py-3 whitespace-nowrap text-xs">{fmtDate(s.created_at)}</td>
              <td className="px-4 py-3">
                <div className="text-white">{s.customer_name}</div>
                <div className="text-zinc-500 text-xs">{s.customer_email}</div>
              </td>
              <td className="px-4 py-3 text-xs max-w-[200px] truncate">{s.description}</td>
              <td className="px-4 py-3 font-semibold text-white">
                {(s.total_with_fees || 0).toFixed(2)} CHF
                {s.fees_applied && <span className="ml-1 text-yellow-400 text-xs">+3%</span>}
                {s.annual && <span className="ml-1 text-green-400 text-xs">-5%</span>}
              </td>
              <td className="px-4 py-3 text-xs">{s.installments}x</td>
              <td className="px-4 py-3"><StatusBadge status={s.payment_status} /></td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  {s.payment_status !== 'paid' ? (
                    <button
                      onClick={() => setConfirmModal({ kind: 'session', id: s.session_id, label: s.customer_name, amount: s.total_with_fees })}
                      className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs rounded-lg font-semibold hover:scale-105 transition-transform inline-flex items-center gap-1"
                      data-testid={`mark-paid-session-${s.session_id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Marquer payée
                    </button>
                  ) : (
                    <span className="text-green-400 text-xs inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> {fmtDate(s.paid_at)}
                    </span>
                  )}
                  <button onClick={() => deletePayment('session', s.session_id)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`delete-session-${s.session_id}`} title="Supprimer">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          )}
        />
      </Section>

      {/* Abonnements coaching & Suivi Pro (Stripe) */}
      <Section title={<><CreditCard className="w-5 h-5 inline mr-2 text-cyan-400" />Abonnements coaching & Suivi Pro ({filteredSubscriptions.length})</>}>
        <PaymentsTable
          rows={filteredSubscriptions}
          columns={['Date', 'Email', 'Formule', 'Montant', 'Statut', 'Accès accordé']}
          renderRow={(t) => (
            <tr key={t.session_id} className="border-t border-white/5 text-zinc-300" data-testid={`subscription-row-${t.session_id}`}>
              <td className="px-4 py-3 whitespace-nowrap text-xs">{fmtDate(t.created_at)}</td>
              <td className="px-4 py-3 text-xs">{t.email}</td>
              <td className="px-4 py-3 text-xs">{PLAN_LABEL[t.plan] || t.plan}</td>
              <td className="px-4 py-3 font-semibold text-white">{(t.amount || 0).toFixed(2)} CHF</td>
              <td className="px-4 py-3"><StatusBadge status={t.payment_status} /></td>
              <td className="px-4 py-3">
                {t.payment_status === 'paid' ? (
                  t.fulfilled ? (
                    <span className="text-green-400 text-xs inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Oui</span>
                  ) : (
                    <span className="text-red-400 text-xs inline-flex items-center gap-1" data-testid={`subscription-not-fulfilled-${t.session_id}`}>
                      <X className="w-3.5 h-3.5" /> Non — sera rattrapé automatiquement (≤15 min)
                    </span>
                  )
                ) : (
                  <span className="text-zinc-500 text-xs">—</span>
                )}
              </td>
            </tr>
          )}
        />
      </Section>

      {/* Confirm Modal */}
      {confirmModal && (
        <ConfirmMarkPaidModal
          modal={confirmModal}
          onCancel={() => setConfirmModal(null)}
          onConfirm={(note) => markPaid(confirmModal.kind, confirmModal.id, note)}
          loading={actionLoading}
        />
      )}

      {/* Facture manuelle */}
      {manualInvoiceModal && (
        <ManualInvoiceModal
          prefill={manualInvoiceModal}
          onClose={() => setManualInvoiceModal(null)}
          onCreated={() => { setManualInvoiceModal(null); loadData(); }}
          fetchWithCsrf={fetchWithCsrf}
        />
      )}
    </div>
  );
};

export const StatCard = ({ label, value, color }) => (
  <div className="bg-zinc-900 border border-white/10 rounded-xl p-4">
    <div className="text-2xl font-extrabold" style={{ color }}>{value}</div>
    <div className="text-xs text-zinc-400 mt-1">{label}</div>
  </div>
);

export const Section = ({ title, children }) => (
  <div className="bg-zinc-900/40 border border-white/10 rounded-2xl p-4 sm:p-5">
    <h3 className="text-white font-semibold mb-3 text-sm">{title}</h3>
    {children}
  </div>
);

export const PaymentsTable = ({ rows, columns, renderRow }) => (
  <div className="overflow-x-auto -mx-4 sm:mx-0">
    <table className="w-full text-sm min-w-[700px]">
      <thead className="bg-zinc-800/70 text-zinc-400">
        <tr>{columns.map((c) => <th key={c} className="text-left px-4 py-3 font-semibold whitespace-nowrap text-xs uppercase tracking-wider">{c}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-zinc-500 text-sm">Aucun résultat</td></tr>
        ) : rows.map(renderRow)}
      </tbody>
    </table>
  </div>
);

export const StatusBadge = ({ status }) => {
  const map = {
    paid: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Payé' },
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'En attente' },
    unpaid: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'En attente' },
    complete: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Complet' },
    open: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Ouvert' },
    expired: { bg: 'bg-zinc-500/20', text: 'text-zinc-400', label: 'Expiré' },
    canceled: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Annulé' },
    scheduled: { bg: 'bg-zinc-500/20', text: 'text-zinc-400', label: 'Programmée' },
  };
  const cfg = map[status] || { bg: 'bg-zinc-600/20', text: 'text-zinc-400', label: status || '—' };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`} data-testid={`status-${status}`}>
      {cfg.label}
    </span>
  );
};

export const ManualInvoiceModal = ({ prefill, onClose, onCreated, fetchWithCsrf }) => {
  const [form, setForm] = useState({
    customer_name: prefill.customer_name || '',
    customer_email: prefill.customer_email || '',
    customer_address: '',
    customer_city: '',
    description: prefill.description || '',
    amount: prefill.amount || '',
    product_type: prefill.product_type || '',
    send_email: true,
    installments_count: 1,
    installments_interval_days: 30,
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async () => {
    if (!form.customer_email.trim() || !form.customer_name.trim() || !form.description.trim() || !form.amount) {
      sonnerToast.error('Nom, email, description et montant sont requis.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/payments/create-manual-invoice`, {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          amount: parseFloat(form.amount),
          product_type: form.product_type || null,
          installments_count: parseInt(form.installments_count, 10) || 1,
          installments_interval_days: parseInt(form.installments_interval_days, 10) || 30,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Erreur');
      const n = json.installments_created?.length || 1;
      sonnerToast.success(
        n > 1
          ? `Plan de paiement créé : ${n} factures (1 envoyée, ${n - 1} programmées automatiquement).`
          : `Facture ${json.invoice_number} créée${json.email_sent ? ' et envoyée par email.' : '.'}`
      );
      onCreated();
    } catch (e) {
      sonnerToast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-white/10 rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="manual-invoice-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white inline-flex items-center gap-2"><FileText className="w-5 h-5 text-primary" /> Facture QR-bill manuelle</h3>
          <button onClick={onClose} data-testid="close-manual-invoice-modal"><X className="w-4 h-4 text-zinc-500 hover:text-white" /></button>
        </div>
        <p className="text-zinc-500 text-xs mb-4">Pour un arrangement de paiement hors self-checkout (ex: mensualité par virement). Génère et envoie un vrai QR-bill, visible ensuite dans Factures/Paiements/Relances.</p>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input value={form.customer_name} onChange={set('customer_name')} placeholder="Nom du client" className="px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none" data-testid="manual-invoice-name" />
            <input value={form.customer_email} onChange={set('customer_email')} type="email" placeholder="Email du client" className="px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none" data-testid="manual-invoice-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input value={form.customer_address} onChange={set('customer_address')} placeholder="Adresse (optionnel)" className="px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none" data-testid="manual-invoice-address" />
            <input value={form.customer_city} onChange={set('customer_city')} placeholder="Ville (optionnel)" className="px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none" data-testid="manual-invoice-city" />
          </div>
          <input value={form.description} onChange={set('description')} placeholder="Description (ex: Coaching en ligne)" className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none" data-testid="manual-invoice-description" />
          <div className="grid grid-cols-2 gap-3">
            <input value={form.amount} onChange={set('amount')} type="number" step="0.05" placeholder="Montant CHF / mensualité" className="px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none" data-testid="manual-invoice-amount" />
            <select value={form.product_type} onChange={set('product_type')} className="px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none" data-testid="manual-invoice-product-type">
              {PRODUCT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-zinc-500 text-xs mb-1 block">Nombre de mensualités</label>
              <input value={form.installments_count} onChange={set('installments_count')} type="number" min="1" max="24" className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none" data-testid="manual-invoice-installments-count" />
            </div>
            <div>
              <label className="text-zinc-500 text-xs mb-1 block">Intervalle (jours)</label>
              <input value={form.installments_interval_days} onChange={set('installments_interval_days')} type="number" min="7" max="90" disabled={Number(form.installments_count) <= 1} className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none disabled:opacity-40" data-testid="manual-invoice-installments-interval" />
            </div>
          </div>
          {Number(form.installments_count) > 1 && (
            <p className="text-emerald-300/80 text-xs bg-emerald-500/5 border border-emerald-500/20 rounded-lg px-3 py-2">
              Plan de paiement : {form.installments_count} factures de {form.amount || '—'} CHF créées maintenant. La 1ère est envoyée tout de suite, les {Number(form.installments_count) - 1} suivantes seront envoyées automatiquement toutes les {form.installments_interval_days} jours, sans action de votre part.
            </p>
          )}
          {['coaching-online', 'coaching-hybride', 'communaute', 'suivi-pro-plus'].includes(form.product_type) && (
            <p className="text-primary/80 text-xs bg-primary/5 border border-primary/20 rounded-lg px-3 py-2">
              Marquer cette facture comme payée prolongera automatiquement l'accès app du client de 30 jours (créera son compte si besoin).
            </p>
          )}
          <label className="flex items-center gap-2 text-zinc-300 text-sm cursor-pointer">
            <input type="checkbox" checked={form.send_email} onChange={set('send_email')} data-testid="manual-invoice-send-email" />
            Envoyer immédiatement la facture par email au client
          </label>
        </div>

        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-sm transition-colors disabled:opacity-50" data-testid="manual-invoice-cancel">Annuler</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 bg-gradient-to-r from-primary to-pink-500 text-white rounded-xl text-sm font-semibold inline-flex items-center gap-2 hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100" data-testid="manual-invoice-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Créer la facture
          </button>
        </div>
      </div>
    </div>
  );
};

export const ConfirmMarkPaidModal = ({ modal, onCancel, onConfirm, loading }) => {
  const [note, setNote] = useState('');
  return (
    <div className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-zinc-900 border border-white/10 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="confirm-mark-paid-modal">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Marquer comme payé ?</h3>
            <p className="text-zinc-400 text-sm mt-1">
              {modal.label} · <strong className="text-white">{(modal.amount || 0).toFixed(2)} CHF</strong>
            </p>
          </div>
        </div>

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 mb-4 flex items-start gap-2">
          <Mail className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-blue-300 text-xs">
            Un email <strong>&quot;Votre forfait est activé !&quot;</strong> sera envoyé automatiquement au client.
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-zinc-300 text-xs font-semibold mb-2">Note interne (optionnel)</label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ex: Virement reçu le 12.02.2026"
            className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-xl text-white text-sm focus:border-primary focus:outline-none"
            data-testid="confirm-mark-paid-note"
          />
        </div>

        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-sm transition-colors disabled:opacity-50"
            data-testid="confirm-mark-paid-cancel"
          >
            Annuler
          </button>
          <button
            onClick={() => onConfirm(note)}
            disabled={loading}
            className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl text-sm font-semibold inline-flex items-center gap-2 hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100"
            data-testid="confirm-mark-paid-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Confirmer et envoyer l&apos;email
          </button>
        </div>
      </div>
    </div>
  );
};
